import { useState, useMemo, memo } from 'react'
import {
  Box,
  Collapse,
  Paper,
  Typography,
  Chip,
  TextField,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import AddIcon from '@mui/icons-material/Add'
import StorageIcon from '@mui/icons-material/Storage'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useSchedulerData } from 'Features/SchedulerView/SchedulerView.data'
import { useCreateScheduleVersion } from 'Features/SchedulerView/SchedulerView.data'
import { useDataStatus, DataStatusResponse } from './Scheduler.data'
import EditIcon from '@mui/icons-material/Edit'
import dayjs from 'dayjs'

type DataSummaryItem = {
  name: string
  count: number
}

const TABLE_DISPLAY_NAMES: Record<string, string> = {
  'packing-routes': 'Packaging Routes',
  'production-requirements': 'Production Requirements',
  'workstation-downtime': 'Workstation Downtime',
  'cleaning-mapping': 'Cleaning Mapping',
  'synchronized-lines': 'Synchronized Lines',
  'resource-and-material-constraints': 'Resource Constraints',
  'resource-capacity-profile': 'Resource Capacity Profile',
  'resource-and-input-mapping': 'Input Mapping',
  'resource-and-output-mapping': 'Output Mapping',
  'base-material-clean-changeover': 'Base Material Changeover',
  'product-clean-changeover': 'Product Clean Changeover',
  tanks: 'Holding Tanks',
  'tank-eligibility': 'Tank Eligibility',
  'tank-week-state': 'Tank Week State',
}

type ScheduleDataVersionsPanelProps = {
  schedulerId: string
  iteration: string
  selectedWeek: number
  dataSummary?: DataSummaryItem[]
  onVersionChange?: (versionKey: string) => void
}

type VersionEntry = {
  key: string
  name: string
  createdAt?: string
  createdBy?: string
}

export const ScheduleDataVersionsPanel = memo(
  ({
    schedulerId,
    iteration,
    selectedWeek,
    dataSummary = [],
    onVersionChange,
  }: ScheduleDataVersionsPanelProps) => {
    const navigate = useNavigate()
    const [versionName, setVersionName] = useState('')
    const {
      data: scheduleData,
      isLoading,
      refetch,
    } = useSchedulerData(schedulerId, iteration)
    const { data: dataStatus, isLoading: isStatusLoading } = useDataStatus(
      schedulerId,
      iteration
    )

    const [expandedTable, setExpandedTable] = useState<string | null>(null)

    const onCreateSuccess = () => {
      toast.success('Version created successfully')
      setVersionName('')
      refetch()
    }
    const onCreateError = () => {
      toast.error('Failed to create version')
    }
    const { mutate: createVersion, isLoading: isCreating } =
      useCreateScheduleVersion(onCreateSuccess, onCreateError)

    const versions = useMemo<VersionEntry[]>(() => {
      if (!scheduleData?.schedule?.versionDetails) return []
      try {
        const parsed = JSON.parse(scheduleData.schedule.versionDetails)
        return Object.keys(parsed)
          .map((key) => ({
            key,
            name: parsed[key]?.name ?? `Version ${key}`,
            createdAt: parsed[key]?.time || parsed[key]?.createdAt,
            createdBy: parsed[key]?.createdBy,
          }))
          .sort((a, b) => {
            const numA = parseInt(a.key, 10)
            const numB = parseInt(b.key, 10)
            if (!isNaN(numA) && !isNaN(numB)) return numB - numA
            return b.key.localeCompare(a.key)
          })
      } catch {
        return []
      }
    }, [scheduleData])

    const handleCreateVersion = () => {
      if (!versionName.trim()) {
        toast.error('Please enter a version name')
        return
      }
      // Calculate hours since last Monday midnight (required by backend)
      const now = dayjs()
      const lastMondayMidnight =
        now.day() === 1
          ? now.startOf('day')
          : now.subtract((now.day() + 6) % 7, 'day').startOf('day')
      const hoursSinceSundayMidnight = now.diff(lastMondayMidnight, 'hour')

      createVersion({
        scheduleId: schedulerId,
        iteration,
        data: {
          name: versionName.trim(),
          hoursSinceSundayMidnight,
          time: now.toISOString(),
        },
      })
    }

    const handleViewVersion = (versionKey: string) => {
      if (onVersionChange) {
        onVersionChange(versionKey)
      } else {
        navigate(`/plan-scheduler/input/${schedulerId}/${versionKey}`)
      }
    }

    return (
      <Box sx={{ width: '100%' }}>
        {/* Section 1: Data Status */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: '#f8fafc',
            }}
          >
            <StorageIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                color: 'primary.main',
              }}
            >
              Data Status
            </Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            {isStatusLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <CircularProgress size={16} />
              </Box>
            ) : dataStatus?.tables ? (
              <>
                {/* Stale data warning */}
                {Object.values(dataStatus.tables).some(
                  (t) => t.modifiedSinceRun
                ) && (
                  <Paper
                    sx={{
                      p: 1,
                      mb: 1,
                      bgcolor: '#fff3e0',
                      border: '1px solid #ffcc80',
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        color: '#e65100',
                      }}
                    >
                      ⚠ Data modified since last schedule run — output may be
                      stale
                    </Typography>
                  </Paper>
                )}
                {Object.entries(dataStatus.tables).map(([apiName, status]) => (
                  <Box key={apiName}>
                    <Box
                      onClick={() =>
                        setExpandedTable(
                          expandedTable === apiName ? null : apiName
                        )
                      }
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                        borderBottom: '1px solid',
                        borderColor: 'rgba(0,0,0,0.06)',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          minWidth: 0,
                        }}
                      >
                        <StorageIcon
                          sx={{
                            fontSize: 12,
                            color: 'text.secondary',
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '0.68rem',
                            color: 'text.primary',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {TABLE_DISPLAY_NAMES[apiName] || apiName}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: '0.6rem', color: 'text.secondary' }}
                        >
                          {status.rowCount}
                        </Typography>
                        {status.modifiedSinceRun ? (
                          <Chip
                            icon={
                              <EditIcon sx={{ fontSize: '10px !important' }} />
                            }
                            label="Edited"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.58rem',
                              bgcolor: '#fff3e0',
                              color: '#e65100',
                              '& .MuiChip-icon': { color: '#e65100' },
                              '& .MuiChip-label': { px: 0.5 },
                            }}
                          />
                        ) : status.rowCount > 0 ? (
                          <Chip
                            icon={
                              <CheckCircleOutlineIcon
                                sx={{ fontSize: '10px !important' }}
                              />
                            }
                            label="Loaded"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.58rem',
                              bgcolor: 'rgba(46, 125, 50, 0.08)',
                              color: 'success.dark',
                              '& .MuiChip-icon': { color: 'success.main' },
                              '& .MuiChip-label': { px: 0.5 },
                            }}
                          />
                        ) : (
                          <Chip
                            icon={
                              <WarningAmberIcon
                                sx={{ fontSize: '10px !important' }}
                              />
                            }
                            label="Empty"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.58rem',
                              bgcolor: 'rgba(0,0,0,0.04)',
                              color: 'text.secondary',
                              '& .MuiChip-icon': { color: 'text.disabled' },
                              '& .MuiChip-label': { px: 0.5 },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    {/* Expandable history */}
                    <Collapse in={expandedTable === apiName}>
                      {status.history && status.history.length > 0 ? (
                        <Box
                          sx={{
                            pl: 3,
                            py: 0.5,
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                          }}
                        >
                          {status.history.map((entry, i) => (
                            <Box
                              key={i}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 0.25,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '0.55rem',
                                  color: 'text.disabled',
                                  minWidth: 120,
                                }}
                              >
                                {entry.timestamp
                                  ? new Date(entry.timestamp).toLocaleString()
                                  : '—'}
                              </Typography>
                              <Chip
                                label={entry.operation || 'unknown'}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.5rem',
                                  fontWeight: 600,
                                  '& .MuiChip-label': { px: 0.4 },
                                  bgcolor:
                                    entry.operation === 'MERGE' ||
                                    entry.operation === 'UPDATE'
                                      ? '#fff3e0'
                                      : entry.operation === 'DELETE'
                                        ? 'rgba(211,47,47,0.08)'
                                        : 'rgba(0,0,0,0.04)',
                                  color:
                                    entry.operation === 'MERGE' ||
                                    entry.operation === 'UPDATE'
                                      ? '#e65100'
                                      : entry.operation === 'DELETE'
                                        ? '#d32f2f'
                                        : 'text.secondary',
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '0.5rem',
                                  color: 'text.disabled',
                                }}
                              >
                                v{entry.version}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            pl: 3,
                            py: 0.5,
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '0.55rem', color: 'text.disabled' }}
                          >
                            No history available
                          </Typography>
                        </Box>
                      )}
                    </Collapse>
                  </Box>
                ))}
                {dataStatus.lastRunTimestamp && (
                  <Typography
                    sx={{
                      fontSize: '0.58rem',
                      color: 'text.disabled',
                      mt: 0.75,
                      textAlign: 'right',
                    }}
                  >
                    Last run:{' '}
                    {new Date(dataStatus.lastRunTimestamp).toLocaleString()}
                  </Typography>
                )}
              </>
            ) : (
              /* Fallback to static dataSummary */
              dataSummary.map((item) => (
                <Box
                  key={item.name}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                    '&:not(:last-child)': {
                      borderBottom: '1px solid',
                      borderColor: 'rgba(0,0,0,0.06)',
                    },
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <StorageIcon
                      sx={{ fontSize: 12, color: 'text.secondary' }}
                    />
                    <Typography sx={{ fontSize: '0.68rem' }}>
                      {item.name}
                    </Typography>
                  </Box>
                  <Chip
                    label={item.count > 0 ? 'Loaded' : 'Empty'}
                    size="small"
                    sx={{ height: 18, fontSize: '0.58rem' }}
                  />
                </Box>
              ))
            )}
          </Box>
        </Paper>

        {/* Section 2: Version History */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: '#f8fafc',
            }}
          >
            <HistoryIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                color: 'primary.main',
              }}
            >
              Snapshots
            </Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1 }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : versions.length === 0 ? (
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  py: 1,
                  textAlign: 'center',
                }}
              >
                No versions found
              </Typography>
            ) : (
              <Box sx={{ position: 'relative', pl: 2 }}>
                {/* Timeline vertical line */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 5,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    bgcolor: 'divider',
                    borderRadius: 1,
                  }}
                />
                {versions.map((version) => {
                  const isCurrent = version.key === iteration
                  return (
                    <Box
                      key={version.key}
                      sx={{
                        position: 'relative',
                        py: 0.75,
                        '&:not(:last-child)': {
                          mb: 0.5,
                        },
                      }}
                    >
                      {/* Timeline dot */}
                      <FiberManualRecordIcon
                        sx={{
                          position: 'absolute',
                          left: -14,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: 10,
                          color: isCurrent ? 'primary.main' : 'divider',
                        }}
                      />
                      <Box
                        onClick={() => handleViewVersion(version.key)}
                        sx={{
                          border: '1px solid',
                          borderColor: isCurrent ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          px: 1.25,
                          py: 0.75,
                          bgcolor: isCurrent
                            ? 'rgba(0, 65, 90, 0.03)'
                            : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'rgba(0, 65, 90, 0.04)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 0.25,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              color: 'text.primary',
                            }}
                          >
                            {version.name}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Chip
                              label={`v${version.key}`}
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.58rem',
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 0.5 },
                              }}
                            />
                            {isCurrent && (
                              <Chip
                                label="Active"
                                size="small"
                                color="primary"
                                sx={{
                                  height: 16,
                                  fontSize: '0.58rem',
                                  fontWeight: 600,
                                  '& .MuiChip-label': { px: 0.5 },
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                        {(version.createdAt || version.createdBy) && (
                          <Typography
                            sx={{
                              fontSize: '0.62rem',
                              color: 'text.secondary',
                            }}
                          >
                            {version.createdAt && version.createdAt}
                            {version.createdAt && version.createdBy && ' - '}
                            {version.createdBy && version.createdBy}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
        </Paper>

        {/* Section 3: Save Snapshot */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: '#f8fafc',
            }}
          >
            <AddIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                color: 'primary.main',
              }}
            >
              Save Snapshot
            </Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1.25 }}>
            <Typography
              sx={{
                fontSize: '0.62rem',
                color: 'text.secondary',
                mb: 1,
                lineHeight: 1.4,
              }}
            >
              Save a copy of all current input data and schedule output. You can
              compare or restore later.
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Snapshot name (e.g. Before promo changes)"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateVersion()
              }}
              sx={{
                mb: 1,
                '& .MuiInputBase-input': { fontSize: '0.72rem', py: '6px' },
                '& .MuiOutlinedInput-root': { borderRadius: '4px' },
              }}
            />
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={
                isCreating ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <AddIcon sx={{ fontSize: '14px !important' }} />
                )
              }
              disabled={isCreating || !versionName.trim()}
              onClick={handleCreateVersion}
              sx={{
                fontSize: '0.68rem',
                textTransform: 'none',
                py: 0.5,
                minHeight: 0,
              }}
            >
              {isCreating ? 'Saving...' : 'Save Snapshot'}
            </Button>
          </Box>
        </Paper>
      </Box>
    )
  }
)

ScheduleDataVersionsPanel.displayName = 'ScheduleDataVersionsPanel'
